import { useEffect, useState } from "react"
import { useWindowEvent } from "./useWindowEvent"

// Keeps the original stitches breakpoint names as the public API (zero
// changes needed at call sites) mapped onto the ported Tailwind screens
// (tailwind.config.ts: sm 600 / md 900 / lg 1200 / xl 1600 / xxl 2000).
// desktop/cinema carry lg's value forward past 1200px in the new scale
// (see tailwind.config.ts's screens comment), so they're mapped onto the
// nearest standard xl/xxl slots rather than their old 1800/2400 values.
const BREAKPOINT_MIN_WIDTH = {
  bigPhone: 600,
  tablet: 900,
  laptop: 1200,
  desktop: 1600,
  cinema: 2000,
} as const

export function useViewportDerived<T>(initial: T, condition: (size: { width: number; height: number }) => T): T {
  const [value, setValue] = useState(initial)

  useEffect(() => {
    setValue(
      condition({
        width: window.innerWidth,
        height: window.innerHeight,
      }),
    )
  }, [])

  useWindowEvent("resize", (e) => {
    setValue(
      condition({
        width: window.innerWidth,
        height: window.innerHeight,
      }),
    )
  })

  return value
}

export function useIsMobile() {
  return useViewportDerived(false, ({ width }) => width < 900)
}

export function useBreakpoint() {
  return useViewportDerived("initial", ({ width }) => {
    let breakpoint = "initial"
    for (const key of Object.keys(BREAKPOINT_MIN_WIDTH) as (keyof typeof BREAKPOINT_MIN_WIDTH)[]) {
      if (width >= BREAKPOINT_MIN_WIDTH[key]) {
        breakpoint = key
      }
    }
    return breakpoint
  })
}

export function useBreakpointIsBelow(name: keyof typeof BREAKPOINT_MIN_WIDTH) {
  return useViewportDerived(false, ({ width }) => width < BREAKPOINT_MIN_WIDTH[name])
}

export function useBreakpointIsFrom(name: keyof typeof BREAKPOINT_MIN_WIDTH) {
  return useViewportDerived(false, ({ width }) => width >= BREAKPOINT_MIN_WIDTH[name])
}
